/**
 * One workflow as one sentence. The editor shows it above the steps and the
 * list shows it under the name, so a definition can be proof-read without
 * opening anything — and the trigger stays what HAPPENED while the narrowing
 * stays a step, which is the distinction the step list makes easy to blur.
 *
 * Pure: every label the sentence needs is passed in, and each lookup is
 * optional so the list (which has no destination query) degrades to counts
 * rather than showing ids.
 */
import { formatClock, formatDayKey } from '../../lib/format.js';
import {
  isScheduleTrigger,
  type BuilderApproverRef,
  type BuilderCondition,
  type BuilderDefinition,
  type BuilderSchedule,
  type BuilderStep,
  type WorkflowTrigger,
} from '../../lib/types.js';
import { humanizeKey } from './definitionEdit.js';

/** `strong` marks the parts a reader scans for — the values, not the grammar. */
export interface SummaryToken {
  text: string;
  strong?: boolean;
}

export interface SummaryLookups {
  triggers?: WorkflowTrigger[];
  /** A scheduled workflow's days and time, so the sentence can say when it runs. */
  schedule?: BuilderSchedule | null;
  destinationLabel?: (id: string) => string | undefined;
  approverLabel?: (ref: BuilderApproverRef) => string | undefined;
}

/** Past two, the names stop helping — the count does. */
const NAMES_SHOWN = 2;
/** A summary is a sentence, not a transcript. */
const MAX_CLAUSES = 5;

const OP_WORDS: Record<BuilderCondition['op'], string> = {
  eq: 'is',
  neq: 'is not',
  contains: 'contains',
  gt: 'is more than',
  lt: 'is less than',
  exists: 'has any value',
  changed: 'changed since the last check',
};

const plain = (text: string): SummaryToken => ({ text });
const strong = (text: string): SummaryToken => ({ text, strong: true });

/** Sentence-cased catalog titles become clauses: "Feedback was triaged" reads
 * as "when feedback was triaged". An all-caps first word (an acronym) stays. */
const asClause = (title: string): string => {
  const [first = ''] = title.split(' ');
  if (first.length > 1 && first === first.toUpperCase()) return title;
  return title.charAt(0).toLowerCase() + title.slice(1);
};

/** The field's own words: `payload.unit.name` reads as "Unit name". */
const fieldLabel = (path: string): string => {
  const last = path.split('.').filter(Boolean).pop() ?? path;
  return humanizeKey(last);
};

const joinNames = (names: string[], fallbackNoun: string): SummaryToken[] => {
  if (names.length === 0) return [plain(`no ${fallbackNoun} yet`)];
  if (names.length <= NAMES_SHOWN) {
    return names.flatMap((n, i) => (i === 0 ? [strong(n)] : [plain(' and '), strong(n)]));
  }
  return [strong(names[0] ?? ''), plain(` and ${String(names.length - 1)} others`)];
};

const destinations = (ids: string[], lookups: SummaryLookups): SummaryToken[] => {
  const names = ids.map((id) => lookups.destinationLabel?.(id)).filter((n): n is string => !!n);
  // No labeller (the list has no destination query) — say how many, never an id.
  if (names.length === 0 && ids.length > 0)
    return [strong(`${String(ids.length)} ${ids.length === 1 ? 'place' : 'places'}`)];
  return joinNames(names, 'destination');
};

const condition = (c: BuilderCondition): SummaryToken[] => {
  const tokens = [strong(fieldLabel(c.path) || 'a field'), plain(` ${OP_WORDS[c.op]}`)];
  if (c.op !== 'exists' && c.op !== 'changed' && c.value !== undefined)
    tokens.push(plain(' '), strong(String(c.value)));
  return tokens;
};

/** The first condition carries the meaning; the rest are "and 2 more checks". */
const conditions = (list: BuilderCondition[], match: 'all' | 'any'): SummaryToken[] => {
  const [first] = list;
  if (!first) return [plain('a check that is not filled in')];
  const rest = list.length - 1;
  return [
    ...condition(first),
    ...(rest > 0
      ? [
          plain(
            ` and ${String(rest)} more ${match === 'any' ? 'alternative' : 'check'}${rest === 1 ? '' : 's'}`,
          ),
        ]
      : []),
  ];
};

const approverPhrase = (refs: BuilderApproverRef[], l: SummaryLookups): SummaryToken[] => {
  const names = refs.map((r) => l.approverLabel?.(r)).filter((n): n is string => !!n);
  if (names.length === 0 && refs.length > 0)
    return [strong(`${String(refs.length)} ${refs.length === 1 ? 'person' : 'people'}`)];
  return joinNames(names, 'approver');
};

/**
 * One step as one clause. `depth` stops a nested fork from unrolling the whole
 * tree into the sentence: inside an arm, a further fork is named, not opened.
 */
const clause = (step: BuilderStep, l: SummaryLookups, depth: number): SummaryToken[] => {
  switch (step.type) {
    case 'filter':
      return [plain('only if '), ...conditions(step.conditions, step.match)];
    case 'notify':
      // The window changes WHEN someone hears, which is load-bearing enough to
      // show in a one-line summary — otherwise a step that holds until Monday
      // reads identically to one that pages at 3am.
      return [
        plain('message '),
        ...destinations(
          [
            ...step.channelIds,
            ...(step.recipients ?? []).map((recipient) => `${recipient.kind}:${recipient.id}`),
          ],
          l,
        ),
        ...(step.deliverWindow ? [plain(' during set hours')] : []),
      ];
    case 'agent': {
      // First line only — a multi-paragraph goal must not unroll into the summary.
      const goal = (step.task.split('\n')[0] ?? '').trim().replace(/\.$/, '');
      return goal
        ? [plain('have an AI agent '), strong(asClause(goal))]
        : [plain('have an AI agent investigate')];
    }
    case 'approval': {
      const ask = [plain('ask '), ...approverPhrase(step.approvers, l), plain(' to approve')];
      if (depth > 0 || step.onApprove.length === 0) return ask;
      return [...ask, plain(', then if they do, '), ...sequence(step.onApprove, l, depth + 1)];
    }
    case 'bot':
      return [
        plain(
          `have a bot ${step.ask ? 'ask' : 'tell'} ${
            step.recipients.kind === 'group'
              ? 'everyone in a group'
              : 'the person named in the event'
          }${step.ask ? ' to decide' : ''}`,
        ),
      ];
    case 'lookup': {
      const inputs = Object.values(step.input)
        .filter((v) => ['string', 'number', 'boolean'].includes(typeof v))
        .map(String);
      return [
        plain('look up '),
        strong(humanizeKey(step.name ?? step.tool).toLowerCase()),
        ...(inputs.length > 0 ? [plain(' for '), ...joinNames(inputs, 'input')] : []),
      ];
    }
    case 'report':
      return [
        plain('run a report'),
        ...(step.sendLink && step.recipients.length > 0 ? [plain(' and send the link')] : []),
      ];
    case 'branch': {
      if (depth > 0) return [plain('do different things depending on the answer')];
      const arms = step.cases.flatMap((c, i) => [
        plain(i === 0 ? 'if ' : '; if '),
        ...conditions(c.when.conditions, c.when.match),
        plain(', '),
        ...sequence(c.steps, l, depth + 1),
      ]);
      const otherwise =
        step.otherwise.length > 0
          ? [plain('; otherwise '), ...sequence(step.otherwise, l, depth + 1)]
          : [];
      return arms.length > 0 ? [...arms, ...otherwise] : [plain('split into cases')];
    }
    // A definition saved under an OLDER step vocabulary (local seeds, ancient
    // snapshots) must read as a sentence, never crash the page.
    default:
      return [plain('run a step this version no longer supports')];
  }
};

/** Steps in order, as clauses — capped so the sentence stays a sentence. */
const sequence = (steps: BuilderStep[], l: SummaryLookups, depth: number): SummaryToken[] => {
  if (steps.length === 0) return [plain('nothing happens')];
  const shown = steps.slice(0, MAX_CLAUSES);
  const tokens = shown.flatMap((s, i) => [
    ...(i === 0 ? [] : [plain(i === shown.length - 1 ? ', then ' : ', ')]),
    ...clause(s, l, depth),
  ]);
  const hidden = steps.length - shown.length;
  return hidden > 0
    ? [...tokens, plain(`, then ${String(hidden)} more step${hidden === 1 ? '' : 's'}`)]
    : tokens;
};

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** 1 → "1st", 22 → "22nd", 13 → "13th". */
const ordinal = (n: number): string => {
  const teen = n % 100 >= 11 && n % 100 <= 13;
  const suffix = teen ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10];
  return `${String(n)}${suffix ?? 'th'}`;
};

const inList = (items: string[]): string =>
  items.length <= 1
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items.at(-1) ?? ''}`;

/** "Every weekday at 7:00 AM", "On the 1st of every month at …", "Once on Oct 21 at …",
 *  "Every 15 minutes". */
export const schedulePhrase = (schedule: BuilderSchedule | null | undefined): SummaryToken[] => {
  if (!schedule) return [strong('On a schedule')];
  if ('everyMinutes' in schedule) {
    const m = schedule.everyMinutes;
    const every =
      m % 60 !== 0 ? `${String(m)} minutes` : m === 60 ? 'hour' : `${String(m / 60)} hours`;
    return [plain('Every '), strong(every)];
  }
  const at = [plain(' at '), strong(formatClock(schedule.time))];
  if ('on' in schedule) return [plain('Once on '), strong(formatDayKey(schedule.on)), ...at];
  if ('monthDays' in schedule)
    return [
      plain('On the '),
      strong(inList(schedule.monthDays.map(ordinal))),
      plain(' of every month'),
      ...at,
    ];
  const days = [...schedule.days].sort((a, b) => a - b);
  const which =
    days.length === 7
      ? 'day'
      : days.join() === '1,2,3,4,5'
        ? 'weekday'
        : inList(days.map((d) => WEEKDAYS[d] ?? ''));
  return [plain('Every '), strong(which), ...at];
};

/** "When <what happened>, <what we do>." */
export const triggerPhrase = (trigger: string, triggers?: WorkflowTrigger[]): SummaryToken[] => {
  if (!trigger) return [plain('something happens')];
  const meta = triggers?.find((t) => t.type === trigger);
  // Platform titles are already clauses ("Feedback was triaged"). A tenant's
  // own event is named after the thing itself ("Unit alarm"), so it needs the
  // verb to read as a sentence — as does a type that has left the catalog and
  // has only its slug left.
  if (meta?.source === 'platform') return [strong(asClause(meta.title))];
  const name = meta ? meta.title : humanizeKey(trigger.replace(/^custom\./, ''));
  return [strong(name), plain(' arrives')];
};

export const summarize = (
  trigger: string,
  definition: BuilderDefinition | null,
  lookups: SummaryLookups = {},
): SummaryToken[] => {
  const steps = definition?.steps ?? [];
  const when = isScheduleTrigger(trigger)
    ? schedulePhrase(lookups.schedule)
    : [plain('When '), ...triggerPhrase(trigger, lookups.triggers)];
  return [...when, plain(', '), ...sequence(steps, lookups, 0), plain('.')];
};

/** The same sentence as plain text — titles, aria labels, tests. */
export const summaryText = (tokens: SummaryToken[]): string => tokens.map((t) => t.text).join('');
